import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import type { ActivationSummary } from "@visioni/a3s-monitoring/app";
import { EMPTY, hasText } from "../format";

/**
 * Patriot's `pendingState` and `completedStatus`, verbatim and side by side.
 *
 * Both are shown because they co-exist on one activation and neither is
 * derivable from the other, so collapsing them to a single open/closed value
 * would hide exactly the combinations support needs to see.
 */
export function ActivationStatus({
  activation,
}: {
  activation: Pick<ActivationSummary, "pendingState" | "completedStatus">;
}) {
  const { pendingState, completedStatus } = activation;

  if (!hasText(pendingState) && !hasText(completedStatus)) {
    return (
      <Text textStyle="xs/regular" color="text">
        {EMPTY}
      </Text>
    );
  }

  return (
    <View flexDirection="row" flexWrap="wrap" gap="xs">
      {hasText(pendingState) && (
        <View style={[styles.chip, styles.pending]} accessibilityLabel={`Pending state ${pendingState}`}>
          <Text textStyle="xxs/semibold" style={styles.pendingText}>
            {pendingState}
          </Text>
        </View>
      )}
      {hasText(completedStatus) && (
        <View style={[styles.chip, styles.completed]} accessibilityLabel={`Completed status ${completedStatus}`}>
          <Text textStyle="xxs/semibold" style={styles.completedText}>
            {completedStatus}
          </Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  chip: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xxs,
    borderRadius: theme.spacing.sm,
    borderWidth: 1,
  },
  pending: { borderColor: theme.colors.status.warning },
  completed: { borderColor: theme.colors.borderCard },
  pendingText: { color: theme.colors.status.warning },
  completedText: { color: theme.colors.textMuted },
}));
